import React, { useState, useEffect, useCallback } from 'react';
import {
  getScenarioPool,
  createScenarioPoolEntry,
  updateScenarioPoolEntry,
  deleteScenarioPoolEntry,
  getScenarioIcons,
} from '../../api/admin';
import AdminLayout from '../../components/AdminLayout';
import Button from '../../components/Button';
import Alert from '../../components/Alert';
import ReportIcon from '../../components/report_v5/ReportIcons';

// 场景候选池行编辑器（V5 报告 Page5「岗位场景应用建议」）。
// 后端: apps/reports/admin_scenario_pool_views.py（IsAdminRole, enterprise_admin 403）。
// 两池: try_first（适合优先尝试）/ be_cautious（需要谨慎）；每池 LLM 各选 6 条进报告。
// 仿 AdminRiskTemplatesPage 的行内编辑风格：有 id 走 update，无 id 走 create。

const TABS = [
  { kind: 'try_first', label: '适合优先尝试', secondaryHeader: '推荐 AI 用法' },
  { kind: 'be_cautious', label: '需要谨慎', secondaryHeader: '建议边界' },
];

// 后端 400 等错误体（dict / list / 字符串）拍平成可读单行。
function formatErr(data, fallback) {
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (Array.isArray(data)) return data.join('；');
  if (typeof data === 'object') {
    if (data.detail) return Array.isArray(data.detail) ? data.detail.join('；') : data.detail;
    const parts = Object.entries(data).map(([k, v]) =>
      `${k}: ${Array.isArray(v) ? v.join('；') : v}`);
    return parts.length ? parts.join('；') : fallback;
  }
  return fallback;
}

let TMP_SEQ = -1; // 新行临时 key（负数，区别于真 id）

export default function AdminScenarioPoolPage() {
  const [tab, setTab] = useState('try_first');
  const [rows, setRows] = useState([]);   // 每行附 draft 编辑态（含 _key 唯一键）
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingKey, setSavingKey] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);

  const activeTab = TABS.find((t) => t.kind === tab) || TABS[0];

  useEffect(() => {
    getScenarioIcons()
      .then(({ data }) => setIcons(Array.isArray(data?.icons) ? data.icons : []))
      .catch(() => {});
  }, []);

  const load = useCallback(async (kind) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await getScenarioPool({ pool_kind: kind });
      const list = Array.isArray(data) ? data : (data.results ?? []);
      setRows(list.map((s) => ({
        _key: s.id,
        id: s.id,
        pool_kind: s.pool_kind,
        name: s.name || '',
        icon_hint: s.icon_hint || '',
        tag: s.tag || '',
        reason: s.reason || '',
        secondary_text: s.secondary_text || '',
        sort_order: s.sort_order ?? 0,
        is_active: s.is_active ?? true,
      })));
    } catch (e) {
      setError(formatErr(e.response?.data, '加载失败'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(tab); }, [load, tab]);

  const patchRow = (key, patch) =>
    setRows((rs) => rs.map((r) => (r._key === key ? { ...r, ...patch } : r)));

  const addRow = () => {
    setError('');
    setSuccess('');
    setRows((rs) => [
      ...rs,
      {
        _key: TMP_SEQ--,
        id: null,
        pool_kind: tab,
        name: '',
        icon_hint: icons[0] || '',
        tag: '',
        reason: '',
        secondary_text: '',
        sort_order: 0,
        is_active: true,
      },
    ]);
  };

  const saveRow = async (row) => {
    if (!row.name?.trim()) { setError('场景名不能为空'); return; }
    setSavingKey(row._key);
    setError('');
    setSuccess('');
    const payload = {
      pool_kind: row.pool_kind,
      name: row.name,
      icon_hint: row.icon_hint,
      tag: row.tag,
      reason: row.reason,
      secondary_text: row.secondary_text,
      sort_order: row.sort_order,
      is_active: row.is_active,
    };
    try {
      if (row.id) {
        await updateScenarioPoolEntry(row.id, payload);
        setSuccess(`已保存：${row.name}`);
      } else {
        const { data } = await createScenarioPoolEntry(payload);
        // 创建成功后把临时行换成带真 id 的行
        patchRow(row._key, { id: data?.id ?? row._key, _key: data?.id ?? row._key });
        setSuccess(`已新增：${row.name}`);
      }
    } catch (e) {
      setError(formatErr(e.response?.data, '保存失败'));
    } finally {
      setSavingKey(null);
    }
  };

  const deleteRow = async (row) => {
    // 未保存的临时行直接从列表抹掉，不打后端
    if (!row.id) {
      setRows((rs) => rs.filter((r) => r._key !== row._key));
      return;
    }
    if (!window.confirm(`确认删除场景「${row.name}」？此操作不可撤销。`)) return;
    setDeletingKey(row._key);
    setError('');
    setSuccess('');
    try {
      await deleteScenarioPoolEntry(row.id);
      setRows((rs) => rs.filter((r) => r._key !== row._key));
      setSuccess(`已删除：${row.name}`);
    } catch (e) {
      // 删到该池 active<6 时后端返回 400 + 文案
      setError(formatErr(e.response?.data, '删除失败'));
    } finally {
      setDeletingKey(null);
    }
  };

  return (
    <AdminLayout title="场景候选池">
      {error && <Alert type="error" style={{ marginBottom: 12 }}>{error}</Alert>}
      {success && <Alert type="success" style={{ marginBottom: 12 }}>{success}</Alert>}

      <div style={{ marginBottom: 16, fontSize: 12, color: '#64748b', lineHeight: 1.7 }}>
        运营维护 V5 报告「岗位场景应用建议」候选池，LLM 从每池各选 <b>6 条</b>写入报告。
        改动<b>立即影响后续新生成的报告</b>；已生成的报告是快照，不受影响——要回溯旧报告需运维跑
        <code style={inlineCode}>repick_v5_scenarios</code> 命令。
        <b> 每池至少保留 6 条启用项</b>，删到不足 6 条会被后端拒绝。
      </div>

      {/* tab 切换 */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {TABS.map((t) => {
          const on = t.kind === tab;
          return (
            <button
              key={t.kind}
              onClick={() => { if (t.kind !== tab) setTab(t.kind); }}
              style={{
                padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', border: on ? '1.5px solid #1f53e6' : '1.5px solid #e2e8f0',
                background: on ? '#1f53e6' : '#fff', color: on ? '#fff' : '#475569',
                transition: 'all 0.15s',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>加载中...</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
            该池暂无候选项，点下方「+ 加一条」新增。
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {/* 「标签」(tag) 列已隐藏：它不进报告、不喂 LLM、不进 schema，纯死字段。
                    DB 字段与已有值保留（load/payload 仍带 tag），仅后台不再展示编辑入口。 */}
                {['场景名', '图标', '推荐理由', activeTab.secondaryHeader, '排序', '启用', '操作'].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r._key} style={{
                  borderBottom: idx < rows.length - 1 ? '1px solid #f1f5f9' : 'none',
                  opacity: r.is_active ? 1 : 0.6,
                }}>
                  <td style={{ ...tdStyle, minWidth: 130 }}>
                    <input type="text" style={inputStyle}
                      value={r.name}
                      onChange={(e) => patchRow(r._key, { name: e.target.value })} />
                  </td>
                  <td style={{ ...tdStyle, width: 150 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {/* 实时预览：选哪个图标，左边立刻渲染对应 SVG（基础版报告里就长这样） */}
                      <span style={iconPreviewStyle} title={r.icon_hint || '未选择'}>
                        <ReportIcon name={r.icon_hint || 'circle'} size={18} />
                      </span>
                      <select style={{ ...inputStyle, flex: 1 }}
                        value={r.icon_hint}
                        onChange={(e) => patchRow(r._key, { icon_hint: e.target.value })}>
                        {/* 当前值不在白名单时也兜底列出，避免被下拉吞掉 */}
                        {r.icon_hint && !icons.includes(r.icon_hint) && (
                          <option value={r.icon_hint}>{r.icon_hint}</option>
                        )}
                        {icons.map((ic) => (
                          <option key={ic} value={ic}>{ic}</option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <textarea
                      style={{ ...inputStyle, height: 72, resize: 'vertical', fontFamily: 'inherit' }}
                      value={r.reason}
                      onChange={(e) => patchRow(r._key, { reason: e.target.value })} />
                  </td>
                  <td style={tdStyle}>
                    <textarea
                      style={{ ...inputStyle, height: 72, resize: 'vertical', fontFamily: 'inherit' }}
                      value={r.secondary_text}
                      onChange={(e) => patchRow(r._key, { secondary_text: e.target.value })} />
                  </td>
                  <td style={{ ...tdStyle, width: 80 }}>
                    <input type="number" step="1" style={{ ...inputStyle, width: 64 }}
                      value={r.sort_order}
                      onChange={(e) => patchRow(r._key, { sort_order: parseInt(e.target.value, 10) || 0 })} />
                  </td>
                  <td style={{ ...tdStyle, width: 56, textAlign: 'center' }}>
                    <input type="checkbox" checked={r.is_active}
                      onChange={(e) => patchRow(r._key, { is_active: e.target.checked })} />
                  </td>
                  <td style={{ ...tdStyle, width: 150 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <Button size="sm" onClick={() => saveRow(r)} disabled={savingKey === r._key}>
                        {savingKey === r._key ? '保存中' : '保存'}
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => deleteRow(r)} disabled={deletingKey === r._key}>
                        {deletingKey === r._key ? '删除中' : '删除'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Button variant="secondary" size="sm" onClick={addRow}>+ 加一条</Button>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>
          {activeTab.label}池共 {rows.length} 条（其中启用 {rows.filter((r) => r.is_active).length} 条）。
        </span>
      </div>
    </AdminLayout>
  );
}

const inputStyle = {
  width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8,
  fontSize: 13, outline: 'none', boxSizing: 'border-box',
};
const thStyle = {
  padding: '10px 14px', textAlign: 'left', fontWeight: 600,
  color: '#475569', borderBottom: '1px solid #e2e8f0',
};
const tdStyle = { padding: '12px 14px', verticalAlign: 'top', color: '#334155' };
const iconPreviewStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 30, height: 30, flexShrink: 0, borderRadius: 8,
  border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#1f53e6',
};
const inlineCode = {
  fontFamily: 'ui-monospace, monospace', fontSize: 11,
  background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, color: '#1a3a6b',
};
